import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authRedirectGuard } from '@core/guards/auth-redirect.guard';
import { twoFactorAuthCompletedGuard } from '@core/guards/two-factor-auth-completed.guard';
import { ErrorPageComponent } from './pages/error-page/error-page.component';
import { xtAnchor } from '@xt/core/router';
import { serialCanActivate } from '@xt/core/utils';
import { QuestionnaireRendererComponent } from '@xt/questionnaire-cdk';
import { userInformationGuard } from '@xt/user-context';
import { hasPermission } from '@xt/core/permissions';
import { AppPermission } from './app-permission';
import { isAuthenticatedGuard } from '@core/guards/is-authenticated.guard';
import { isMobileDeviceGuard } from '@core/guards/is-mobile-device.guard';
import { DashboardShellComponent } from './pages/dashboard/dashboard-shell/dashboard-shell.component';
import { CustomPreloadingStrategy } from './customPreloadingStrategy';
import { authFullUserGuard } from '@core/guards/auth-full-user.guard';
import { waitForTokenGuard } from '@core/guards/wait-for-token.guard';
import { nativeClientGuard } from '@core/guards/native-client.guard';

export const QUESTIONNAIRE_RENDERER_PATH = 'questionnaire-renderer';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./pages/dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: 'embeddable',
    canActivate: [
      serialCanActivate(
        nativeClientGuard,
        waitForTokenGuard,
        authFullUserGuard,
        userInformationGuard,
        hasPermission(AppPermission.Dashboard),
      ),
    ],
    loadChildren: () =>
      import('./pages/embeddable/embeddable.module').then(
        (m) => m.EmbeddableModule,
      ),
  },
  {
    path: 'profile',
    canActivate: [authRedirectGuard],
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('@features/profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: QUESTIONNAIRE_RENDERER_PATH,
    canActivate: [
      serialCanActivate(
        isAuthenticatedGuard,
        userInformationGuard,
        hasPermission(AppPermission.QuestionnaireRenderer),
      ),
    ],
    component: QuestionnaireRendererComponent,
  },
  {
    path: 'privacy-policy-cms',
    loadComponent: () =>
      import('./pages/privacy-policy/privacy-policy.component').then(
        (c) => c.PrivacyPolicyComponent,
      ),
  },
  {
    path: 'no-app-access',
    data: {
      preload: false,
      delay: false,
    },
    loadComponent: () =>
      import('./pages/no-app-access/no-app-access.component').then(
        (c) => c.NoAppAccessComponent,
      ),
  },
  {
    path: 'no-access',
    data: {
      preload: false,
      delay: false,
    },
    loadComponent: () =>
      import('./pages/no-access/no-access.component').then(
        (c) => c.NoAccessComponent,
      ),
  },
  {
    path: 'start-verification-app',
    canActivate: [twoFactorAuthCompletedGuard],
    data: {
      preload: false,
      delay: false,
    },
    loadComponent: () =>
      import('./pages/start-verification-app/start-verification-app').then(
        (c) => c.StartVerificationApp,
      ),
  },
  {
    path: 'auth',
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('./pages/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'pin-brief',
    canActivate: [twoFactorAuthCompletedGuard],
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('@features/pin-brief/pin-brief.module').then(
        (m) => m.PinBriefModule,
      ),
  },
  {
    path: 'onboarding',
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('@features/onboarding/onboarding.module').then(
        (m) => m.OnboardingModule,
      ),
  },
  {
    path: 'interests',
    canActivate: [authRedirectGuard],
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('@features/onboarding/interest-wizard/interest-wizard.module').then(
        (m) => m.InterestWizardModule,
      ),
  },
  {
    path: 'two-factor',
    canActivate: [authRedirectGuard],
    data: {
      preload: false,
      delay: false,
    },
    loadChildren: () =>
      import('@features/onboarding/two-factor/two-factor.module').then(
        (m) => m.TwoFactorModule,
      ),
  },
  {
    path: 'identification',
    component: DashboardShellComponent,
    canActivate: [
      serialCanActivate(
        isAuthenticatedGuard,
        userInformationGuard,
        isMobileDeviceGuard(),
      ),
    ],
    data: {
      [xtAnchor]: 'identification',
    },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@features/xt-verification/identification-feature/xt-identification.module').then(
            (m) => m.XtIdentificationModule,
          ),
      },
    ],
  },
  {
    // do not lazy load so component is there if network is off
    path: 'error',
    data: {
      preload: false,
      delay: false,
    },
    component: ErrorPageComponent,
  },
  // a fix for CMS select-activation-role link
  {
    path: 'select-activation-role',
    redirectTo: 'identification',
  },
  {
    path: '**',
    redirectTo: 'error',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      urlUpdateStrategy: 'deferred',
      preloadingStrategy: CustomPreloadingStrategy,
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
